import TextArea from "../../../../Components/Common/TextArea/TextArea";
import Input from "../../../../Components/Common/Input/Input";
import Select from "Components/Common/Select/Select";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormik, getIn, FormikErrors, FormikTouched } from "formik";
import * as Yup from "yup";
import Button from "../../../../Components/Common/Button/Button";
import { uploadFileToS3 } from "../../GcPreconCommercial/UploadFileToS3";
import SearchableSelect from "Components/Common/SearchableSelect/SearchableSelect";
import { getSubcontractors } from "services";
import useDebounce from "hooks/useDebounce";
import useScrollToTop from "hooks/useScrollToTop";
import DateSelect from "../../../../Components/Common/DateSelect/DateSelect";
import {
  gcNextLabels,
  gcSteps,
  reasonOptions,
  requiredChecklist,
  tradeOptions,
} from "../constants/changeOrderData";
import type { SovLienItem } from "../constants/changeOrderData";
import Table, { type Column } from "Components/Table/Table";
import GcStepBar from "./GcStepBar";
import PlusIcon from "Assets/Img/PlusIcon";
import Card from "Pages/GcPrecon/GcProjectDetails/components/Card";
import QuickAddGrid from "Pages/GcPrecon/GcProjectDetails/components/sections/SOVSetup/components/QuickAddGrid";
import TickIcon from "Assets/Img/TickIcon";
import { useChangeOrderPdf } from "hooks/useChangeOrderPdf";

const TRADE_OPTIONS = tradeOptions.map((o) => ({ value: o, label: o }));
const REASON_OPTIONS = reasonOptions.map((o) => ({ value: o, label: o }));

// ── Public types ───────────────────────────────────────────────────────────────

export interface Attachment {
  fileName: string;
  key: string;
  title: string;
}

export interface WizardLineItem {
  id: number;
  aiaItemId: string;
  aiaItemCode: string | number;
  description: string;
  amount: string;
  subContractorId?: string;
  subContractorName?: string;
}

export interface WizardFormValues {
  project: string;
  coNumber: string;
  date: string;
  title: string;
  trade: string;
  reason: string;
  scheduleImpact: string;
  contractDateImpact: string;
  description: string;
  scopeNotes: string;
  lineItems: WizardLineItem[];
  attachments: Attachment[];
  checklist: Record<string, boolean>;
  lender?: { companyName?: string };
}

// ── Internal types ─────────────────────────────────────────────────────────────

interface RawSubcontractor {
  id?: string | number;
  companyName?: string;
}

interface VendorOption {
  id: string;
  name: string;
  subtitle?: string;
}

interface Division {
  code: string;
  label: string;
  name: string;
  id: string;
  colorHex?: string;
  isIncludeBudgetBreakdown?: boolean;
}

const getDivisionCode = (value: unknown): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (value && typeof value === "object") {
    const v = value as Record<string, unknown>;
    return String(v.code || v.value || v.id || v.itemCode || v.name || "");
  }
  return "";
};

interface GcWizardProps {
  showForm: boolean;
  step: number;
  submitted: boolean;
  isViewMode?: boolean;
  onShowForm: () => void;
  onHideForm: () => void;
  onGoStep: (step: number) => void;
  onNext: () => void;
  onBack: () => void;
  aiaItems: {
    id: string;
    itemCode: number | string;
    itemName: string;
    itemLabel?: unknown;
  }[];
  aiaHasMore?: boolean;
  loadingMoreAia?: boolean;
  onLoadMoreAia?: () => void;
  sovLienItems?: SovLienItem[];
  onSubmit: (values: WizardFormValues) => Promise<void>;
  onSaveDraft: (values: WizardFormValues) => Promise<void>;
  onToast: (message: string) => void;
  disableNewCO?: boolean;
  initialValues?: Partial<WizardFormValues>;
  projectName?: string; // undefined = loading, "" = not found
  estCompletionDate?: string;
  originalContractValue?: number;
  approvedCOsTotal?: number;
  revisedContractWithPendingCO?: number;
  onProjectedContractChange?: (value: number) => void;
}

// ── Yup schemas ────────────────────────────────────────────────────────────────

const step1Schema = Yup.object({
  coNumber: Yup.string().trim().required("CO Number is required."),
  date: Yup.string().required("Date is required."),
  title: Yup.string().trim().required("Change Order Title is required."),
  trade: Yup.string()
    .required("Trade / Division is required.")
    .notOneOf(["Select trade..."], "Trade / Division is required."),
  reason: Yup.string()
    .required("Reason / Cause is required.")
    .notOneOf(["Select reason..."], "Reason / Cause is required."),
  scheduleImpact: Yup.string()
    .required("Schedule Impact is required.")
    .matches(/^[+-]?\d+$/, "Enter a valid number of days (e.g. +3 or -5)."),
  description: Yup.string()
    .trim()
    .required("Description of Change is required."),
});

const lineItemSchema = Yup.object({
  aiaItemId: Yup.string().required("AIA Item is required."),
  description: Yup.string().trim().required("Description is required."),
  amount: Yup.string()
    .required("Amount is required.")
    .test(
      "positive",
      "Amount must be greater than zero.",
      (val) => Number(val ?? 0) > 0,
    ),
  subContractorId: Yup.string().required("Subcontractor / Vendor is required."),
});

const step2Schema = Yup.object({
  lineItems: Yup.array()
    .of(lineItemSchema)
    .min(1, "At least one line item is required."),
});

const step3Schema = Yup.object({
  attachments: Yup.array().min(
    1,
    "At least one supporting document is required.",
  ),
  checklist: Yup.mixed<Record<string, boolean>>().test(
    "all-checked",
    "All checklist items must be checked.",
    (value) =>
      requiredChecklist.every(
        (label) => !!(value as Record<string, boolean>)?.[label],
      ),
  ),
});

const stepSchemas = [step1Schema, step2Schema, step3Schema, Yup.object()];

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ── Utilities ──────────────────────────────────────────────────────────────────

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(value) || 0,
  );

function normalizeSubcontractor(option: RawSubcontractor): VendorOption {
  const id = String(option?.id ?? "");
  const name = option?.companyName ?? "";
  return { id: id || name, name };
}

/**
 * Converts Yup's flat path strings (e.g. "lineItems[0].aiaItemId") into
 * nested objects compatible with Formik's setErrors / setTouched.
 */
function buildNestedFromYup(err: Yup.ValidationError): {
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
} {
  const errors: Record<string, unknown> = {};
  const touched: Record<string, unknown> = {};

  const setPath = (
    target: Record<string, unknown>,
    path: string,
    value: unknown,
  ) => {
    const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
    let node: Record<string, unknown> = target;
    parts.forEach((key, i) => {
      if (i === parts.length - 1) {
        node[key] = value;
      } else {
        const nextIsIndex = !isNaN(Number(parts[i + 1]));
        if (node[key] == null) node[key] = nextIsIndex ? [] : {};
        node = node[key] as Record<string, unknown>;
      }
    });
  };

  err.inner.forEach((e) => {
    if (!e.path) return;
    setPath(errors, e.path, e.message);
    setPath(touched, e.path, true);
  });

  return { errors, touched };
}

// ── Component ──────────────────────────────────────────────────────────────────

const GcWizard = ({
  showForm,
  step,
  submitted,
  isViewMode = false,
  onShowForm,
  onHideForm,
  onGoStep,
  onNext,
  onBack,
  aiaItems,
  aiaHasMore,
  loadingMoreAia,
  onLoadMoreAia,
  sovLienItems = [],
  onSubmit,
  onSaveDraft,
  onToast,
  disableNewCO = false,
  initialValues: externalInitial,
  projectName,
  estCompletionDate,
  originalContractValue = 0,
  approvedCOsTotal = 0,
  revisedContractWithPendingCO,
  onProjectedContractChange,
}: GcWizardProps) => {
  useScrollToTop(step);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // ── Vendor state ───────────────────────────────────────────────────────────

  const PAGE_SIZE = 10;
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorHasMore, setVendorHasMore] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorLoadParams, setVendorLoadParams] = useState({
    search: "",
    page: 1,
  });
  const hasFetchedVendors = useRef(false);
  const hasSeededLineItems = useRef(false);
  const debouncedVendorSearch = useDebounce(vendorSearch, 400);

  useEffect(() => {
    if (!hasFetchedVendors.current) return;
    setVendorLoadParams({ search: debouncedVendorSearch, page: 1 });
  }, [debouncedVendorSearch]);

  useEffect(() => {
    if (!hasFetchedVendors.current) return;
    const { search: s, page } = vendorLoadParams;
    let cancelled = false;
    const load = async () => {
      setVendorLoading(true);
      const { data, error }: { data: any; error: any } =
        await getSubcontractors(s.trim(), page, PAGE_SIZE);
      if (cancelled) return;
      if (!error) {
        const list: RawSubcontractor[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const normalized = list
          .map(normalizeSubcontractor)
          .filter((o) => o.id && o.name);
        setVendorOptions((prev) =>
          page === 1 ? normalized : [...prev, ...normalized],
        );
        setVendorHasMore((data?.pagination?.total ?? 0) > page * PAGE_SIZE);
      }
      setVendorLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [vendorLoadParams]);

  const handleVendorDropdownOpen = () => {
    if (hasFetchedVendors.current) return;
    hasFetchedVendors.current = true;
    setVendorLoadParams({ search: "", page: 1 });
  };

  // ── Formik ─────────────────────────────────────────────────────────────────

  const formik = useFormik<WizardFormValues>({
    initialValues: {
      project: "Aroma Joes - Portland",
      coNumber: "CO-001",
      date: new Date().toISOString().split("T")[0],
      title: "",
      trade: "Select trade...",
      reason: "Select reason...",
      scheduleImpact: "",
      contractDateImpact: "",
      description: "",
      scopeNotes: "",
      lineItems: [],
      attachments: [],
      checklist: Object.fromEntries(requiredChecklist.map((l) => [l, false])),
      ...externalInitial,
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  // Pre-populate line items from active SOV when they arrive (async from parent)
  // Skip if draft already provided line items via externalInitial
  useEffect(() => {
    if (hasSeededLineItems.current || sovLienItems.length === 0) return;
    hasSeededLineItems.current = true;
    if (formik.values.lineItems.length > 0) return;
    formik.setFieldValue(
      "lineItems",
      sovLienItems.map((item, index) => ({
        id: Date.now() + index,
        aiaItemId: item.aiaItemId,
        aiaItemCode: item.aiaItemCode,
        description: item.description,
        amount: String(item.amount?.amount ?? 0),
        subContractorId: item.subContractorId ?? "",
        subContractorName: item.subContractor?.companyName ?? "",
      })),
    );
  }, [sovLienItems]);

  // Sync project name from parent into Formik when it resolves
  const { setFieldValue } = formik;
  useEffect(() => {
    if (projectName === undefined) return;
    setFieldValue("project", projectName || "Project Not Found");
  }, [projectName, setFieldValue]);

  // Auto-calculate contractDateImpact whenever scheduleImpact or estCompletionDate changes
  useEffect(() => {
    if (!estCompletionDate) return;
    const days = parseInt(formik.values.scheduleImpact) || 0;
    setFieldValue("contractDateImpact", addDays(estCompletionDate, days));
  }, [formik.values.scheduleImpact, estCompletionDate, setFieldValue]);

  // ── Error helpers ──────────────────────────────────────────────────────────

  /** Single top-level field error (shown after touched). */
  const fieldErr = (field: keyof WizardFormValues): string | undefined => {
    const e = formik.errors[field];
    const t = formik.touched[field];
    return t && typeof e === "string" ? e : undefined;
  };

  /** Line-item field error (shown after that cell is touched). */
  const lineItemErr = (
    index: number,
    field: keyof WizardLineItem,
  ): string | undefined => {
    const e = getIn(formik.errors, `lineItems[${index}].${field}`);
    const t = getIn(formik.touched, `lineItems[${index}].${field}`);
    return t && e ? e : undefined;
  };

  /** Array-level line-items error ("at least one required"). */
  const lineItemsMinErr: string | undefined = (() => {
    const e = formik.errors.lineItems;
    const t = formik.touched.lineItems;
    return t && typeof e === "string" ? e : undefined;
  })();

  const attachmentsErr: string | undefined = (() => {
    const e = formik.errors.attachments;
    const t = formik.touched.attachments;
    return t && typeof e === "string" ? e : undefined;
  })();

  const checklistTouched = !!getIn(formik.touched, "checklist");

  /** Clear one field error when the user starts editing (better UX). */
  const clearErr = (path: string) => {
    if (getIn(formik.errors, path)) {
      formik.setFieldError(path, undefined);
    }
  };

  // ── Step validation ────────────────────────────────────────────────────────

  const runStepValidation = async (stepNum: number): Promise<boolean> => {
    const schema = stepSchemas[stepNum - 1];
    try {
      await schema.validate(formik.values, { abortEarly: false });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const { errors, touched } = buildNestedFromYup(err);
        await formik.setErrors({
          ...formik.errors,
          ...errors,
        } as FormikErrors<WizardFormValues>);
        await formik.setTouched(
          { ...formik.touched, ...touched } as FormikTouched<WizardFormValues>,
          false,
        );
      }
      return false;
    }
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>(
        ".hasError, .field-error, .cellError",
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const handleNext = async () => {
    if (isViewMode) {
      onNext();
      return;
    }
    const hasVisibleCharError = Array.from(
      document.querySelectorAll<HTMLElement>(".field-error"),
    ).some((el) => el.offsetParent !== null);
    if (hasVisibleCharError) {
      const el = Array.from(
        document.querySelectorAll<HTMLElement>(".field-error"),
      ).find((el) => el.offsetParent !== null);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (await runStepValidation(step)) {
      onNext();
    } else {
      scrollToFirstError();
    }
  };

  const handleSubmitClick = async () => {
    let allErrors: Record<string, unknown> = {};
    let allTouched: Record<string, unknown> = {};
    let firstErrStep = 0;

    for (let s = 1; s <= 3; s++) {
      try {
        await stepSchemas[s - 1].validate(formik.values, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          if (firstErrStep === 0) firstErrStep = s;
          const { errors, touched } = buildNestedFromYup(err);
          allErrors = { ...allErrors, ...errors };
          allTouched = { ...allTouched, ...touched };
        }
      }
    }

    if (firstErrStep > 0) {
      await formik.setErrors(allErrors as FormikErrors<WizardFormValues>);
      await formik.setTouched(
        allTouched as FormikTouched<WizardFormValues>,
        false,
      );
      onGoStep(firstErrStep);
      return;
    }

    formik.handleSubmit();
  };

  const handleSaveDraftClick = async () => {
    const valid = await runStepValidation(step);
    if (!valid) scrollToFirstError();
    onSaveDraft(formik.values);
  };

  // ── File upload ────────────────────────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const result: {
        key?: string;
        Key?: string;
        data?: { key?: string; Key?: string };
      } = await uploadFileToS3(file);
      const key =
        result?.key ??
        result?.Key ??
        result?.data?.key ??
        result?.data?.Key ??
        "";
      formik.setFieldValue("attachments", [
        ...formik.values.attachments,
        { fileName: file.name, key, title: file.name },
      ]);
      clearErr("attachments");
    } catch (err) {
      onToast(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (key: string) => {
    formik.setFieldValue(
      "attachments",
      formik.values.attachments.filter((a) => a.key !== key),
    );
  };

  // ── Line item operations ───────────────────────────────────────────────────

  const updateLineItems = (next: WizardLineItem[]) =>
    formik.setFieldValue("lineItems", next);

  const addLineItem = () =>
    updateLineItems([
      ...formik.values.lineItems,
      {
        id: Date.now(),
        aiaItemId: "",
        aiaItemCode: "",
        description: "",
        amount: "",
      },
    ]);

  const deleteLineItem = (id: number) =>
    updateLineItems(formik.values.lineItems.filter((i) => i.id !== id));

  const updateLineItemField = (
    id: number,
    field: keyof WizardLineItem,
    value: string | number,
  ) =>
    updateLineItems(
      formik.values.lineItems.map((i) =>
        i.id === id ? { ...i, [field]: value } : i,
      ),
    );

  const selectAiaItem = (
    id: number,
    aiaItemId: string,
    aiaItemCode: string | number,
  ) =>
    updateLineItems(
      formik.values.lineItems.map((i) =>
        i.id === id ? { ...i, aiaItemId, aiaItemCode } : i,
      ),
    );

  const addLineItemFromSOV = (item: SovLienItem) =>
    updateLineItems([
      ...formik.values.lineItems,
      {
        id: Date.now(),
        aiaItemId: item.aiaItemId,
        aiaItemCode: item.aiaItemCode,
        description: item.description,
        amount: "",
      },
    ]);

  // ── Computed ───────────────────────────────────────────────────────────────

  const totalAmount = useMemo(
    () =>
      formik.values.lineItems.reduce((s, i) => s + (Number(i.amount) || 0), 0),
    [formik.values.lineItems],
  );

  // Original scheduled value per AIA item, from the SOV snapshot used to seed the table —
  // used to isolate this CO's actual dollar impact from unedited carried-over rows
  const originalAmountByAiaId = useMemo(() => {
    const map = new Map<string, number>();
    sovLienItems.forEach((item) => {
      if (item.aiaItemId)
        map.set(item.aiaItemId, Number(item.amount?.amount ?? 0));
    });
    return map;
  }, [sovLienItems]);

  // Net dollar impact of this CO: sum of (current row amount - original SOV amount).
  // Rows carried over unedited from the SOV net to 0; new/added rows count in full.
  const coImpactAmount = useMemo(
    () =>
      formik.values.lineItems.reduce((s, i) => {
        const original = i.aiaItemId
          ? (originalAmountByAiaId.get(i.aiaItemId) ?? 0)
          : 0;
        return s + ((Number(i.amount) || 0) - original);
      }, 0),
    [formik.values.lineItems, originalAmountByAiaId],
  );
  const aiaAsDivisions = useMemo(
    () =>
      aiaItems.map((a) => {
        const divisionCode = getDivisionCode(a.itemLabel);
        return {
          id: a.id,
          code: a.id,
          label: divisionCode
            ? `${divisionCode} - ${a.itemCode}`
            : String(a.itemCode),
          name: a.itemName,
        };
      }),
    [aiaItems],
  );

  const addedAiaCodes = useMemo(
    () =>
      new Set(formik.values.lineItems.map((i) => i.aiaItemId).filter(Boolean)),
    [formik.values.lineItems],
  );

  // IDs already chosen across all line item rows — used to disable duplicates in dropdowns
  const selectedAiaIds = useMemo(
    () =>
      new Set(formik.values.lineItems.map((i) => i.aiaItemId).filter(Boolean)),
    [formik.values.lineItems],
  );

  const handleQuickAddToggle = (division: Division) => {
    if (addedAiaCodes.has(division.code)) return;
    const aiaItem = aiaItems.find((a) => a.id === division.code);
    addLineItemFromSOV({
      aiaItemId: division.code,
      aiaItemCode: aiaItem?.itemCode ?? division.label,
      description: aiaItem?.itemName ?? "",
    });
  };

  const baseContract = originalContractValue;
  const projectedContract =
    originalContractValue + approvedCOsTotal + coImpactAmount;
  const { values } = formik;

  const { exportChangeOrderPdf, isExportingCoPdf } = useChangeOrderPdf();

  const handleDownloadPdf = async () => {
    await exportChangeOrderPdf({
      projectName,
      coNumber: values.coNumber,
      date: values.date,
      title: values.title,
      trade: values.trade,
      reason: values.reason,
      scheduleImpact: values.scheduleImpact,
      contractDateImpact: values.contractDateImpact,
      description: values.description,
      scopeNotes: values.scopeNotes,
      lineItems: values.lineItems.map((item) => ({
        aiaItemCode: item.aiaItemCode,
        description: item.description,
        amount: Number(item.amount) || 0,
        subContractorName: item.subContractorName,
      })),
      totalAmount,
      attachments: values.attachments.map((att) => ({
        fileName: att.fileName,
      })),
      checklist: values.checklist,
      baseContract,
      approvedCOsTotal,
      coImpactAmount,
      projectedContract,
      lender: values.lender,
    });
  };

  useEffect(() => {
    onProjectedContractChange?.(projectedContract);
  }, [projectedContract, onProjectedContractChange]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!showForm) {
    return (
      <div className="newCoWrap">
        <Button
          className="btn btnPrimary"
          onClick={onShowForm}
          disabled={disableNewCO}
        >
          <PlusIcon /> Add New Change Order
        </Button>
      </div>
    );
  }

  return (
    <section className="gcWizard">
      <GcStepBar steps={gcSteps} currentStep={step} onGoStep={onGoStep} />

      {!submitted ? (
        <>
          {/* ── Step 1: CO Details ───────────────────────────────────────── */}
          {step === 1 ? (
            <div className="card">
              <div className="cardTitle">
                Change Order Details - {values.coNumber}
                {isViewMode ? "" : " (Draft)"}
              </div>

              <div className="fieldRow c3">
                <div className="field">
                  <label>Project</label>
                  <Input
                    className="fieldInput"
                    value={
                      projectName === undefined ? "Loading..." : values.project
                    }
                    readOnly
                  />
                </div>

                <div className="field">
                  <label>
                    CO Number<span className="reqStar">*</span>
                  </label>
                  <Input
                    id="coNumber"
                    className={`fieldInput${fieldErr("coNumber") ? " hasError" : ""}`}
                    value={values.coNumber}
                    readOnly={isViewMode}
                    onChange={(e) => {
                      formik.setFieldValue("coNumber", e.target.value);
                      clearErr("coNumber");
                    }}
                  />
                  {fieldErr("coNumber") && (
                    <span className="field-error">{fieldErr("coNumber")}</span>
                  )}
                </div>

                <div className="field" id="date">
                  <label>
                    Date<span className="reqStar">*</span>
                  </label>
                  <DateSelect
                    value={values.date}
                    disabled={isViewMode}
                    onChange={(val) => {
                      formik.setFieldValue("date", val);
                      clearErr("date");
                    }}
                  />
                  {fieldErr("date") && (
                    <span className="field-error">{fieldErr("date")}</span>
                  )}
                </div>
              </div>

              <div className="fieldRow c2">
                <div className="field">
                  <label>
                    Change Order Title<span className="reqStar">*</span>
                  </label>
                  <Input
                    id="title"
                    className={`fieldInput${fieldErr("title") ? " hasError" : ""}`}
                    value={values.title}
                    placeholder="Brief descriptive title"
                    readOnly={isViewMode}
                    onChange={(e) => {
                      formik.setFieldValue("title", e.target.value);
                      clearErr("title");
                    }}
                  />
                  {fieldErr("title") && (
                    <span className="field-error">{fieldErr("title")}</span>
                  )}
                </div>

                <div className="field">
                  <label>
                    Trade / Division<span className="reqStar">*</span>
                  </label>
                  <Select
                    id="trade"
                    className={`selectBar ${fieldErr("trade") ? " hasError" : ""}`}
                    options={TRADE_OPTIONS}
                    value={values.trade}
                    disabled={isViewMode}
                    onChange={(e) => {
                      formik.setFieldValue("trade", e.target.value);
                      clearErr("trade");
                    }}
                    height={"36px"}
                  />
                  {fieldErr("trade") && (
                    <span className="field-error">{fieldErr("trade")}</span>
                  )}
                </div>
              </div>

              <div className="fieldRow c3">
                <div className="field">
                  <label>
                    Reason / Cause<span className="reqStar">*</span>
                  </label>
                  <Select
                    id="reason"
                    className={`selectBar${fieldErr("reason") ? " hasError" : ""}`}
                    options={REASON_OPTIONS}
                    value={values.reason}
                    disabled={isViewMode}
                    onChange={(e) => {
                      formik.setFieldValue("reason", e.target.value);
                      clearErr("reason");
                    }}
                    height={"36px"}
                  />
                  {fieldErr("reason") && (
                    <span className="field-error">{fieldErr("reason")}</span>
                  )}
                </div>

                <div className="field">
                  <label>
                    Schedule Impact (days)<span className="reqStar">*</span>
                  </label>
                  <Input
                    id="scheduleImpact"
                    className={`fieldInput${fieldErr("scheduleImpact") ? " hasError" : ""}`}
                    value={values.scheduleImpact}
                    placeholder="e.g. +3 or -5"
                    inputMode="numeric"
                    readOnly={isViewMode}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (
                        val === "" ||
                        val === "+" ||
                        val === "-" ||
                        /^[+-]?\d+$/.test(val)
                      ) {
                        formik.setFieldValue("scheduleImpact", val);
                        clearErr("scheduleImpact");
                      }
                    }}
                  />
                  {fieldErr("scheduleImpact") && (
                    <span className="field-error">
                      {fieldErr("scheduleImpact")}
                    </span>
                  )}
                </div>

                <div className="field">
                  <label>Contract Date Impact</label>
                  <Input
                    className="fieldInput readOnlyField"
                    value={values.contractDateImpact}
                    readOnly
                  />
                </div>
              </div>

              <div className="field">
                <label>
                  Description of Change<span className="reqStar">*</span>
                </label>
                <TextArea
                  id="description"
                  className={`fieldTextarea${fieldErr("description") ? " hasError" : ""}`}
                  value={values.description}
                  placeholder="Describe what changed and why..."
                  readOnly={isViewMode}
                  onChange={(e) => {
                    formik.setFieldValue("description", e.target.value);
                    clearErr("description");
                  }}
                  maxChars={CHAR_LIMIT.DESCRIPTION}
                />
                {fieldErr("description") && (
                  <span className="field-error">{fieldErr("description")}</span>
                )}
              </div>
            </div>
          ) : null}

          {/* ── Step 2: Line Items ───────────────────────────────────────── */}
          {step === 2 ? (
            <>
              {!isViewMode && (
                <Card
                  title="Add by CSI Division"
                  subtitle="Click to add a line item for that division"
                >
                  <QuickAddGrid
                    divisions={aiaAsDivisions}
                    addedCodes={addedAiaCodes}
                    onToggle={handleQuickAddToggle}
                    hasMore={aiaHasMore}
                    loadingMore={loadingMoreAia}
                    onLoadMore={onLoadMoreAia}
                  />
                </Card>
              )}

              {lineItemsMinErr && (
                <div id="lineItems_min">
                  <span className="field-error">{lineItemsMinErr}</span>
                </div>
              )}

              <div className="coTableCard">
                <div className="coTableHeader">
                  <h4>Line Items &amp; Scope of Work</h4>
                  {!isViewMode && (
                    <Button
                      className="btn btnSecondary btnSm"
                      onClick={addLineItem}
                    >
                      Add Line Item
                    </Button>
                  )}
                </div>

                {(() => {
                  const cols: Column<WizardLineItem>[] = [
                    {
                      header: (
                        <>
                          A<span className="tableSubTitle">Item #</span>
                        </>
                      ),
                      className: "center",
                      render: (_, idx) => idx + 1,
                    },
                    {
                      header: "CSI Code",
                      render: (item, idx) => {
                        const selectedAia = aiaItems.find(
                          (a) => a.id === item.aiaItemId,
                        );
                        return (
                          <>
                            <SearchableSelect
                              id={`lineItem_${idx}_aiaItemId`}
                              value={item.aiaItemId}
                              valueLabel={
                                selectedAia
                                  ? `${selectedAia.itemCode} — ${selectedAia.itemName}`
                                  : ""
                              }
                              options={aiaItems}
                              getOptionValue={(a) => a.id}
                              getOptionLabel={(a) =>
                                `${a.itemCode} — ${a.itemName}`
                              }
                              placeholder="Select AIA item…"
                              error={!!lineItemErr(idx, "aiaItemId")}
                              isOptionDisabled={(a) =>
                                selectedAiaIds.has(a.id) &&
                                a.id !== item.aiaItemId
                              }
                              disabled={isViewMode}
                              portal
                              onChange={(id, selected) => {
                                selectAiaItem(
                                  item.id,
                                  id,
                                  selected?.itemCode ?? "",
                                );
                                clearErr(`lineItems[${idx}].aiaItemId`);
                              }}
                            />
                            {lineItemErr(idx, "aiaItemId") && (
                              <span className="cellError">
                                {lineItemErr(idx, "aiaItemId")}
                              </span>
                            )}
                          </>
                        );
                      },
                    },
                    {
                      header: (
                        <>
                          B
                          <span className="tableSubTitle">
                            Description of Work
                          </span>
                        </>
                      ),
                      render: (item, idx) => (
                        <>
                          <Input
                            id={`lineItem_${idx}_description`}
                            className={`coTableInput${lineItemErr(idx, "description") ? " hasError" : ""}`}
                            value={item.description}
                            placeholder="Describe scope of work"
                            readOnly={isViewMode}
                            onChange={(e) => {
                              updateLineItemField(
                                item.id,
                                "description",
                                e.target.value,
                              );
                              clearErr(`lineItems[${idx}].description`);
                            }}
                            onBlur={() =>
                              formik.setFieldTouched(
                                `lineItems[${idx}].description`,
                                true,
                                false,
                              )
                            }
                          />
                          {lineItemErr(idx, "description") && (
                            <span className="cellError">
                              {lineItemErr(idx, "description")}
                            </span>
                          )}
                        </>
                      ),
                    },
                    {
                      header: (
                        <>
                          C
                          <span className="tableSubTitle">Scheduled Value</span>
                        </>
                      ),
                      className: "num",
                      render: (item, idx) => (
                        <>
                          <Input
                            id={`lineItem_${idx}_amount`}
                            className={`coTableInput num${lineItemErr(idx, "amount") ? " hasError" : ""}`}
                            value={item.amount}
                            inputMode="decimal"
                            readOnly={isViewMode}
                            onKeyDown={(e) => {
                              const nav = [
                                "Backspace",
                                "Delete",
                                "Tab",
                                "ArrowLeft",
                                "ArrowRight",
                                "ArrowUp",
                                "ArrowDown",
                                "Home",
                                "End",
                              ];
                              if (nav.includes(e.key)) return;
                              if (
                                (e.ctrlKey || e.metaKey) &&
                                ["a", "c", "v", "x"].includes(e.key)
                              )
                                return;
                              if (!/[\d.]/.test(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val !== "" && !/^\d*\.?\d*$/.test(val))
                                return;
                              updateLineItemField(item.id, "amount", val);
                              clearErr(`lineItems[${idx}].amount`);
                            }}
                            onBlur={() =>
                              formik.setFieldTouched(
                                `lineItems[${idx}].amount`,
                                true,
                                false,
                              )
                            }
                          />
                          {lineItemErr(idx, "amount") && (
                            <span className="cellError">
                              {lineItemErr(idx, "amount")}
                            </span>
                          )}
                        </>
                      ),
                    },
                    {
                      header: "% of CONTRACT",
                      className: "num",
                      render: (item) => {
                        const pct =
                          totalAmount > 0
                            ? (
                                (Number(item.amount) / totalAmount) *
                                100
                              ).toFixed(1)
                            : "0.0";
                        return `${pct}%`;
                      },
                    },
                    {
                      header: "Subcontractor / Vendor",
                      render: (item, idx) => (
                        <>
                          <SearchableSelect
                            id={`lineItem_${idx}_vendor`}
                            value={item.subContractorId || ""}
                            valueLabel={item.subContractorName || ""}
                            options={vendorOptions}
                            getOptionValue={(v: VendorOption) => v.id}
                            getOptionLabel={(v: VendorOption) =>
                              v.subtitle ? `${v.name} (${v.subtitle})` : v.name
                            }
                            placeholder={
                              vendorLoading
                                ? "Loading subcontractors..."
                                : "Search subcontractor..."
                            }
                            error={!!lineItemErr(idx, "subContractorId")}
                            disabled={isViewMode || vendorLoading}
                            onOpen={handleVendorDropdownOpen}
                            loading={vendorLoading}
                            onSearch={(val: string) => setVendorSearch(val)}
                            apiSearch
                            hasMore={vendorHasMore && !vendorLoading}
                            onLoadMore={() =>
                              setVendorLoadParams((prev) => ({
                                ...prev,
                                page: prev.page + 1,
                              }))
                            }
                            onChange={(
                              id: string,
                              vendor: VendorOption | null,
                            ) => {
                              updateLineItems(
                                formik.values.lineItems.map((i) =>
                                  i.id === item.id
                                    ? {
                                        ...i,
                                        subContractorId: id,
                                        subContractorName: vendor
                                          ? vendor.name
                                          : "",
                                      }
                                    : i,
                                ),
                              );
                              clearErr(`lineItems[${idx}].subContractorId`);
                            }}
                            portal
                          />
                          {lineItemErr(idx, "subContractorId") && (
                            <span className="cellError">
                              {lineItemErr(idx, "subContractorId")}
                            </span>
                          )}
                        </>
                      ),
                    },
                    ...(!isViewMode
                      ? [
                          {
                            header: "Actions",
                            className: "center",
                            render: (item: WizardLineItem) => (
                              <div className="coTableActions">
                                <Button
                                  type="button"
                                  className="coRowBtn"
                                  onClick={() => deleteLineItem(item.id)}
                                >
                                  ×
                                </Button>
                              </div>
                            ),
                          },
                        ]
                      : []),
                  ];
                  const footer: React.ReactNode[] = [
                    <td key="label" colSpan={3} className="coTableLabel">
                      Total Change Order Amount
                    </td>,
                    <span key="total" className="coTableAmount">
                      {formatCurrency(totalAmount)}
                    </span>,
                    <span key="pct" className="muted">
                      {totalAmount > 0 ? "100.0%" : "0.0%"}
                    </span>,
                    null,
                    null,
                  ];
                  return (
                    <Table<WizardLineItem>
                      data={values.lineItems}
                      columns={cols}
                      footerRow={footer}
                      tableClassName="coTable"
                      footerRowClassName="coTableFooter"
                    />
                  );
                })()}
              </div>

              <div className="card">
                <div className="field">
                  <label>Additional Scope Notes</label>
                  <TextArea
                    className="fieldTextarea"
                    placeholder="Subcontractor quote details, materials specifications, or other relevant scope information…"
                    value={values.scopeNotes}
                    readOnly={isViewMode}
                    onChange={(e) =>
                      formik.setFieldValue("scopeNotes", e.target.value)
                    }
                    maxChars={CHAR_LIMIT.DESCRIPTION}
                  />
                </div>
              </div>
            </>
          ) : null}

          {/* ── Step 3: Documents + Checklist ───────────────────────────── */}
          {step === 3 ? (
            <>
              <div className="card">
                <div className="cardTitle">Supporting Documents*</div>
                <p className="sectionSub">
                  Attach all relevant backup including subcontractor quotes,
                  photos, specifications, or correspondence.
                </p>
                {!isViewMode && (
                  <>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    <Button
                      type="button"
                      className={`uploadArea${attachmentsErr ? " hasError" : ""}`}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <span className="uploadTitle">
                        {uploading ? "Uploading…" : "Click to attach documents"}
                      </span>
                      <span className="uploadSub">
                        PDF, XLSX, JPG, PNG — up to 25MB each
                      </span>
                    </Button>
                    {attachmentsErr && (
                      <span className="field-error">{attachmentsErr}</span>
                    )}
                  </>
                )}

                <div className="fileChipWrap">
                  {values.attachments.map((att) => (
                    <span key={att.key || att.fileName} className="fileChip">
                      {att.fileName}
                      {!isViewMode && (
                        <Button
                          type="button"
                          className="fileChipRemove"
                          onClick={() => handleRemoveFile(att.key)}
                          aria-label={`Remove ${att.fileName}`}
                        >
                          ×
                        </Button>
                      )}
                    </span>
                  ))}
                </div>
              </div>

              <div className="card">
                <div className="cardTitle">
                  Required Checklist Before Submission*
                </div>
                <div className="checkListWrap">
                  {requiredChecklist.map((label) => (
                    <div key={label}>
                      <label className="checkItem">
                        <Input
                          type="checkbox"
                          checked={values.checklist[label] || false}
                          disabled={isViewMode}
                          onChange={() => {
                            formik.setFieldValue("checklist", {
                              ...values.checklist,
                              [label]: !values.checklist[label],
                            });
                          }}
                        />
                        {label}
                      </label>
                      {checklistTouched && !values.checklist[label] && (
                        <span className="field-error">
                          This item must be checked.
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {/* ── Step 4: Review & Submit ──────────────────────────────────── */}
          {step === 4 ? (
            <div className="card">
              <div className="cardTitle">Review &amp; Submit</div>

              <div className="reviewGrid">
                <div>
                  <span>Project:</span>
                  <strong>{values.project}</strong>
                </div>
                <div>
                  <span>CO Number:</span>
                  <strong>{values.coNumber}</strong>
                </div>
                <div>
                  <span>Date:</span>
                  <strong>{values.date}</strong>
                </div>
                <div>
                  <span>Trade:</span>
                  <strong>{values.trade}</strong>
                </div>
                <div>
                  <span>Reason:</span>
                  <strong>{values.reason}</strong>
                </div>
                <div>
                  <span>Schedule Impact:</span>
                  <strong>{values.scheduleImpact || "None"}</strong>
                </div>
                <div>
                  <span>Total Amount:</span>
                  <strong className="amountPos">
                    {formatCurrency(totalAmount)}
                  </strong>
                </div>
                <div>
                  <span>Documents:</span>
                  <strong>{values.attachments.length} attached</strong>
                </div>
              </div>

              <div className="impactBox">
                Original Contract: {formatCurrency(baseContract)} | Approved
                COs: {formatCurrency(approvedCOsTotal)} | This CO:{" "}
                {formatCurrency(coImpactAmount)} | New Revised Contract:{" "}
                <strong>{formatCurrency(projectedContract)}</strong>
              </div>

              <div className="certificationBox">
                By submitting this change order, you certify that the work
                described constitutes a legitimate change to the contract scope,
                that the cost breakdown is accurate and reflects actual costs,
                and that this change has been communicated to all relevant
                parties. CSC New England will be notified of this submission.
                The project owner has final approval authority.
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="card successWrap">
          <div className="successCircle">
            <TickIcon />
          </div>
          <h3>Change Order Submitted</h3>
          <p>
            {values.coNumber} has been submitted to the owner for approval.
            CSC New England has been notified.
          </p>
          <div className="successActions">
            <Button className="btn btnPrimary" onClick={onHideForm}>
              Back to CO Log
            </Button>
            <Button
              className="btn btnSecondary"
              disabled={isExportingCoPdf}
              onClick={handleDownloadPdf}
            >
              {isExportingCoPdf ? "Generating PDF…" : "Download PDF"}
            </Button>
          </div>
        </div>
      )}

      {showForm && !submitted ? (
        <div className="pubBar">
          <p className="pubInfo">
            Step {step} of 4 - {gcSteps[step - 1]}
          </p>
          <div className="pubActions">
            {step > 1 ? (
              <Button className="btn btnSecondary" onClick={onBack}>
                Back
              </Button>
            ) : null}
            {!isViewMode && (
              <Button
                className="btn btnSecondary"
                onClick={handleSaveDraftClick}
              >
                Save Draft
              </Button>
            )}
            {step < 4 ? (
              <Button
                className="btn btnPrimary"
                onClick={handleNext}
                disabled={uploading}
              >
                {uploading ? "Uploading…" : gcNextLabels[step - 1]}
              </Button>
            ) : !isViewMode ? (
              <Button className="btn btnApprove" onClick={handleSubmitClick}>
                Submit to Owner
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default GcWizard;
